/**
 * Где встать карточке, чтобы точка, о которой она рассказывает, осталась видна.
 *
 * До этого модуля каждая карточка карты сдвигалась на свои сто пикселей
 * «влево-вверх от курсора» — и накрывала собой ровно то место, по которому
 * кликнули: карточка отсчёта шириной 244 и высотой в три сотни пикселей от
 * сдвига в 110 никуда с точки не уходила. Отсюда правило, которое сильнее
 * остальных: ПРЯМОУГОЛЬНИК КАРТОЧКИ НЕ СОДЕРЖИТ ТОЧКУ. Держаться кромок
 * полотна — желание; на полотне меньше самой карточки оно уступает, потому что
 * ради видимости точки карточку и отводят.
 *
 * Модуль чистый: на входе пиксели, на выходе пиксели. Проекция координат в
 * пиксели и рисование — снаружи (`MapCallout`), иначе правило размещения
 * пришлось бы проверять браузером.
 */

/** Просвет между точкой и кромкой карточки: метка, поводок и воздух. Меньше
 *  двух радиусов метки просвет делать нельзя — поводка не станет видно вовсе. */
export const CALLOUT_GAP = 26
/** Отступ от кромки полотна. */
export const CALLOUT_EDGE = 8
/** Радиус метки точки — от неё, а не из центра, стартует линия-поводок. */
export const CALLOUT_MARKER_R = 8

/** Куда отведена карточка от точки. */
export type CalloutSide = 'right' | 'left' | 'below' | 'above'

export interface CalloutInput {
  /** Точка на полотне карты (пиксели контейнера). */
  anchor: { x: number; y: number }
  /** Замеренный размер карточки. */
  size: { width: number; height: number }
  /** Размер полотна карты, а не окна: слева от карты живёт рельс разделов. */
  viewport: { width: number; height: number }
  gap?: number
  edge?: number
  markerR?: number
}

export interface CalloutPlacement {
  left: number
  top: number
  side: CalloutSide
  /** Поводок: от кромки метки до ближайшего места карточки. */
  line: { x1: number; y1: number; x2: number; y2: number }
}

const SIDES: CalloutSide[] = ['right', 'left', 'below', 'above']

/**
 * Долгота привязки в том же обороте мира, что и центр карты.
 *
 * В базе долгота знаковая, а дальневосточные районы едут за 180-й меридиан
 * (см. «Долгота и 180-й меридиан» в CLAUDE.md). MapLibre проецирует то, что
 * дали: знаковые −179,5° при центре 190° легли бы на целый мир левее экрана, и
 * метка с поводком уехали бы неизвестно куда. Сдвигаем на целые обороты — то
 * же самое, что `to_east` на сервере, только относительно центра кадра.
 */
export function anchorLng(lng: number, centerLng: number): number {
  return lng + 360 * Math.round((centerLng - lng) / 360)
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi))
}

export function placeCallout(input: CalloutInput): CalloutPlacement {
  const { anchor, size, viewport } = input
  const gap = input.gap ?? CALLOUT_GAP
  const edge = input.edge ?? CALLOUT_EDGE
  const markerR = input.markerR ?? CALLOUT_MARKER_R

  // Сколько места остаётся по каждую сторону от точки, если просвет соблюдён.
  const room: Record<CalloutSide, number> = {
    right: viewport.width - edge - (anchor.x + gap),
    left: anchor.x - gap - edge,
    below: viewport.height - edge - (anchor.y + gap),
    above: anchor.y - gap - edge,
  }
  const need = (s: CalloutSide) => (s === 'right' || s === 'left' ? size.width : size.height)

  // Порядок предпочтения: вбок, потом вниз/вверх. Карточки кабинета выше, чем
  // шире, и вбок они уезжают, не закрывая полосу карты над точкой и под ней.
  // Не влезла ни одна сторона (окно меньше карточки) — берём ту, где теснит
  // меньше всего; просвет у точки при этом сохраняется в любом случае.
  const side =
    SIDES.find(s => room[s] >= need(s)) ??
    SIDES.reduce((best, s) => (room[s] - need(s) > room[best] - need(best) ? s : best), SIDES[0])

  // Ось «от точки»: просвет держится ВСЕГДА — это и есть правило непокрытия.
  // Дальняя от точки кромка при этом может уехать за полотно; ближняя стоит на
  // месте, поэтому дорисовка содержимого растит карточку прочь от точки, а не
  // на неё.
  let left: number
  let top: number
  if (side === 'right' || side === 'left') {
    left = side === 'right' ? anchor.x + gap : anchor.x - gap - size.width
    // Поперечная ось: карточка по центру точки, но внутри полотна. Нижний упор
    // сильнее верхнего — на полотне ниже карточки она начинается от кромки.
    top = Math.max(edge, Math.min(anchor.y - size.height / 2, viewport.height - size.height - edge))
  } else {
    top = side === 'below' ? anchor.y + gap : anchor.y - gap - size.height
    left = Math.max(edge, Math.min(anchor.x - size.width / 2, viewport.width - size.width - edge))
  }

  // Точка ушла за кадр (карту сдвинули) — отводить карточку не от чего, и
  // держится она полотна: уехав следом за точкой, она забрала бы с собой всё,
  // что человек в этот момент читает. Поводок при этом упирается в кромку и
  // показывает, в какой стороне точка осталась.
  if (
    anchor.x < 0 ||
    anchor.y < 0 ||
    anchor.x > viewport.width ||
    anchor.y > viewport.height
  ) {
    left = Math.max(edge, Math.min(left, viewport.width - size.width - edge))
    top = Math.max(edge, Math.min(top, viewport.height - size.height - edge))
  }

  return { left, top, side, line: leaderLine(anchor, { left, top, ...size }, markerR) }
}

/**
 * Поводок от метки до ближайшего к точке места карточки.
 *
 * Ближайшее место — проекция точки на прямоугольник карточки. У зажатой кромкой
 * карточки это угол, и поводок приходит именно в него, а не в середину стороны,
 * которой рядом с точкой уже нет.
 */
function leaderLine(
  anchor: { x: number; y: number },
  rect: { left: number; top: number; width: number; height: number },
  markerR: number,
): CalloutPlacement['line'] {
  const x2 = clamp(anchor.x, rect.left, rect.left + rect.width)
  const y2 = clamp(anchor.y, rect.top, rect.top + rect.height)
  const dist = Math.hypot(x2 - anchor.x, y2 - anchor.y) || 1
  return {
    x1: anchor.x + ((x2 - anchor.x) / dist) * markerR,
    y1: anchor.y + ((y2 - anchor.y) / dist) * markerR,
    x2,
    y2,
  }
}

/** Насколько карточку утащили мышью от её собственного места, пиксели. */
export interface CalloutDrag {
  dx: number
  dy: number
}

/** Куда смотрит карточка из точки — по её новому месту, а не по прежнему. */
function sideOf(
  anchor: { x: number; y: number },
  rect: { left: number; top: number; width: number; height: number },
): CalloutSide {
  const dx = rect.left + rect.width / 2 - anchor.x
  const dy = rect.top + rect.height / 2 - anchor.y
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left'
  return dy >= 0 ? 'below' : 'above'
}

/**
 * Размещение с учётом перетаскивания мышью.
 *
 * Правило непокрытия отводит карточку САМО, и спорить с ним не о чем — пока
 * человеку не нужна вода ИМЕННО под карточкой. Тогда он уводит её сам, и это
 * его решение: ни просвет у точки, ни выбранная сторона его не ограничивают.
 *
 * Удерживается силой ровно одно — полотно карты: карточка, утащенная за край,
 * исчезает вместе с тем, что человек в ней читал. Упор ближней кромки сильнее
 * дальней, иначе на полотне меньше самой карточки границы переворачиваются и
 * она уезжает вверх сама, без всякого перетаскивания.
 *
 * Метка при этом НЕ ЕДЕТ: она стоит на точке, о которой карточка рассказывает,
 * и поводок пересчитывается до нового места — иначе увезённая карточка
 * показывала бы пальцем в чужую воду.
 */
export function dragCallout(
  base: CalloutPlacement,
  drag: CalloutDrag,
  input: CalloutInput,
): CalloutPlacement {
  const { anchor, size, viewport } = input
  const edge = input.edge ?? CALLOUT_EDGE
  const markerR = input.markerR ?? CALLOUT_MARKER_R
  if (drag.dx === 0 && drag.dy === 0) return base

  const left = Math.max(edge, Math.min(base.left + drag.dx, viewport.width - size.width - edge))
  const top = Math.max(edge, Math.min(base.top + drag.dy, viewport.height - size.height - edge))
  const rect = { left, top, ...size }
  return { left, top, side: sideOf(anchor, rect), line: leaderLine(anchor, rect, markerR) }
}
